// src/components/Groups/SimpleEditableStudentGrid.tsx

import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Chip,
  TextField,
  Button,
  Tooltip,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { ProcessedStudentWithDuplicates } from './utils/duplicateDetection';
import { Student } from '../../Types/Student';
import { simpleDuplicateValidator, ValidationResult } from './utils/simpleDuplicateValidation';

interface SimpleEditableStudentGridProps {
  students: ProcessedStudentWithDuplicates[];
  existingStudents: Student[];
  onStudentsChange: (students: ProcessedStudentWithDuplicates[]) => void;
  onRemoveStudent: (index: number) => void;
}

interface EditedValues {
  [key: number]: Partial<ProcessedStudentWithDuplicates>;
}

const SimpleEditableStudentGrid: React.FC<SimpleEditableStudentGridProps> = ({
  students,
  existingStudents,
  onStudentsChange,
  onRemoveStudent,
}) => {
  const [editedValues, setEditedValues] = useState<EditedValues>({});
  const [autoSaveTimeouts, setAutoSaveTimeouts] = useState<{ [key: number]: NodeJS.Timeout }>({});

  // Validate all students
  const validationResults = useMemo(() => {
    return students.map(student => 
      simpleDuplicateValidator.validateStudent(student, existingStudents)
    );
  }, [students, existingStudents]);

  const validCount = validationResults.filter(r => r.isValid).length;
  const invalidCount = validationResults.filter(r => !r.isValid).length;

  // Initialize edited values for new students
  React.useEffect(() => {
    const newEditedValues: EditedValues = {};
    students.forEach((student, index) => {
      if (!editedValues[index]) {
        newEditedValues[index] = { ...student };
      }
    });
    if (Object.keys(newEditedValues).length > 0) {
      setEditedValues(prev => ({ ...prev, ...newEditedValues }));
    }
  }, [students.length]); // Only run when student count changes

  const handleSave = useCallback((index: number) => {
    const editedStudent = editedValues[index];
    
    if (editedStudent) {
      const updatedStudents = [...students];
      updatedStudents[index] = { 
        ...updatedStudents[index], 
        ...editedStudent 
      };
      onStudentsChange(updatedStudents);
    }
  }, [editedValues, students, onStudentsChange]);

  // Clear auto-save timeout when component unmounts
  React.useEffect(() => {
    return () => {
      Object.values(autoSaveTimeouts).forEach(timeout => {
        clearTimeout(timeout);
      });
    };
  }, [autoSaveTimeouts]);

  const handleFieldChange = useCallback((index: number, field: string, value: string) => {
    // Clear existing timeout for this field
    if (autoSaveTimeouts[index]) {
      clearTimeout(autoSaveTimeouts[index]);
    }

    // Update the edited values
    setEditedValues(prev => ({
      ...prev,
      [index]: {
        ...prev[index],
        [field]: value,
      }
    }));

    // Set new auto-save timeout
    const timeoutId = setTimeout(() => {
      handleSave(index);
      setAutoSaveTimeouts(prev => {
        const newTimeouts = { ...prev };
        delete newTimeouts[index];
        return newTimeouts;
      });
    }, 1000); // 1 second delay

    setAutoSaveTimeouts(prev => ({
      ...prev,
      [index]: timeoutId
    }));
  }, [autoSaveTimeouts, handleSave]);

  const renderEditableCell = (
    index: number, 
    field: keyof ProcessedStudentWithDuplicates, 
    value: any,
    type: string = 'text'
  ) => {
    const editedValue = editedValues[index]?.[field];
    const currentValue = editedValue !== undefined ? editedValue : value;

    return (
      <TextField
        size="small"
        type={type}
        value={currentValue || ''}
        onChange={(e) => handleFieldChange(index, field as string, e.target.value)}
        onBlur={() => handleSave(index)} // Save on blur for immediate feedback
        fullWidth
        variant="outlined"
        InputLabelProps={type === 'date' ? { shrink: true } : undefined}
        sx={{
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.02)',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
            '&.Mui-focused': {
              backgroundColor: 'white',
            }
          }
        }}
      />
    );
  };

  const getStatusChip = (validation: ValidationResult) => {
    if (validation.isValid) {
      return (
        <Chip
          icon={<CheckCircleIcon />}
          label="Ready to Save"
          color="success"
          size="small"
        />
      );
    } else {
      return (
        <Chip
          icon={<ErrorIcon />}
          label="Needs Info"
          color="error"
          size="small"
        />
      );
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">
          Review Students ({students.length})
        </Typography>
        
        <Box display="flex" gap={1} alignItems="center">
          <Chip 
            label={`${validCount} Ready`} 
            color="success" 
            size="small" 
          />
          {invalidCount > 0 && (
            <Chip 
              label={`${invalidCount} Need Info`} 
              color="error" 
              size="small" 
            />
          )}
        </Box>
      </Box>

      {invalidCount > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>{invalidCount} student{invalidCount > 1 ? 's' : ''} {invalidCount === 1 ? 'has' : 'have'} duplicate information</strong>
            <br />
            Students with identical names must have at least one different field (middle name, date of birth, grade, or parent email).
            <br />
            <strong>Suggestions:</strong> Add a middle name, date of birth, or parent email to distinguish between students.
            <br />
            Click the edit button to add missing information.
          </Typography>
        </Alert>
      )}

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Status</TableCell>
              <TableCell>First Name</TableCell>
              <TableCell>Last Name</TableCell>
              <TableCell>Middle Name</TableCell>
              <TableCell>Grade</TableCell>
              <TableCell>Date of Birth</TableCell>
              <TableCell>Parent Email</TableCell>
              <TableCell>Remove</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {students.map((student, index) => {
              const validation = validationResults[index];

              return (
                <TableRow key={index}>
                  <TableCell>
                    <Box display="flex" flexDirection="column" gap={0.5}>
                      {validation.isValid ? (
                        getStatusChip(validation)
                      ) : (
                        <Tooltip title={validation.suggestions.join('; ')} arrow>
                          <Chip
                            icon={<ErrorIcon />}
                            label="Needs Info"
                            color="error"
                            size="small"
                            sx={{ cursor: 'help' }}
                          />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  
                  <TableCell>
                    {renderEditableCell(index, 'firstName', student.firstName)}
                  </TableCell>
                  
                  <TableCell>
                    {renderEditableCell(index, 'lastName', student.lastName)}
                  </TableCell>
                  
                  <TableCell>
                    {renderEditableCell(index, 'middleName', student.middleName)}
                  </TableCell>
                  
                  <TableCell>
                    {renderEditableCell(index, 'gradeLevel', student.gradeLevel)}
                  </TableCell>
                  
                  <TableCell>
                    {renderEditableCell(index, 'dateOfBirth', student.dateOfBirth, 'date')}
                  </TableCell>
                  
                  <TableCell>
                    {renderEditableCell(index, 'parentEmail', student.parentEmail, 'email')}
                  </TableCell>
                  
                  <TableCell>
                    <Box display="flex" gap={0.5}>
                      <Tooltip title="Remove student">
                        <IconButton 
                          size="small" 
                          onClick={() => onRemoveStudent(index)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {students.length === 0 && (
        <Box textAlign="center" py={4}>
          <Typography variant="body2" color="text.secondary">
            No students to review
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default SimpleEditableStudentGrid;
